import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BoutonPrincipal,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import { useTaillePage } from '../lib/useTaillePage';
import {
  useAlertes,
  useDeclencherPassage,
  useStatuerAlerte,
  type AlerteDeclenchee,
  type RegleAlerte,
} from '../lib/useAlertes';
import { ErreurApi } from '../lib/api';

function horodatage(valeur: string): string {
  return new Date(valeur).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

const TONS_CANAL: Record<string, string> = {
  IN_APP: 'info',
  EMAIL: 'info',
  SMS: 'mint',
};

const LIBELLES_CANAL: Record<string, string> = {
  IN_APP: 'Appli',
  EMAIL: 'Mail',
  SMS: 'SMS',
};

/**
 * Écran « Gestion des alertes » (CDC §11).
 *
 * Module transverse : pour chaque règle, les destinataires, les canaux,
 * l'activation site par site et les délais. Refus par défaut — une règle non
 * activée sur un site n'y déclenche rien. Les règles se lisent ici ; elles se
 * règlent par la direction seule, dans « Paramétrage » (TF-435). Les pôles y
 * traitent leurs alertes.
 */
export function AlertesPage(): JSX.Element {
  const { site } = usePerimetre();
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const { data, isLoading, error } = useAlertes(site, page, taillePage);
  const statuer = useStatuerAlerte();
  const passage = useDeclencherPassage();

  const compteurs = data?.compteurs;

  const dernieresAffichees = useMemo(() => data?.dernieres ?? [], [data?.dernieres]);

  useEffect(() => {
    setPage(1);
  }, [site, taillePage]);

  const total = data?.pagination.total ?? 0;
  const pages = data?.pagination.pages ?? 1;
  // Le serveur ramène la page dans les bornes si la dernière s'est vidée.
  const pageCourante = data?.pagination.page ?? page;
  useEffect(() => {
    if (pageCourante !== page) setPage(pageCourante);
  }, [pageCourante, page]);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Gestion des alertes"
        sousTitre="Module transverse · activation par site et par profil destinataire"
        actions={
          <>
            {data?.peutRegler && (
              <Link
                to="/dir-parametres?onglet=alertes"
                className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
              >
                Régler les alertes
              </Link>
            )}
            <BoutonPrincipal
              onClick={() => passage.mutate()}
              disabled={passage.isPending}
            >
              {passage.isPending ? 'Évaluation…' : 'Évaluer maintenant'}
            </BoutonPrincipal>
          </>
        }
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Règles d’alerte"
          valeur={compteurs?.regles ?? '—'}
          detail="catalogue du chapitre 11"
          ton="navy"
        />
        <Compteur
          libelle="Actives sur ce périmètre"
          valeur={compteurs?.activesSurPerimetre ?? '—'}
          detail="activation par site"
          ton="mint"
        />
        <Compteur
          libelle="Déclenchées aujourd’hui"
          valeur={compteurs?.declencheesAujourdHui ?? '—'}
          detail="passage quotidien à 6 h"
          ton="blue"
        />
        <Compteur
          libelle="Ouvertes à traiter"
          valeur={compteurs?.ouvertes ?? '—'}
          detail="en attente d’une suite"
          ton="warm"
        />
      </div>

      {passage.data && (
        <div className="rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
          {passage.data.dossiersExamines} dossier
          {passage.data.dossiersExamines > 1 ? 's' : ''} examiné
          {passage.data.dossiersExamines > 1 ? 's' : ''} · {passage.data.alertesOuvertes} alerte
          {passage.data.alertesOuvertes > 1 ? 's' : ''} ouverte
          {passage.data.alertesOuvertes > 1 ? 's' : ''} · {passage.data.alertesInchangees}{' '}
          déjà en cours · {passage.data.destinatairesPrevenus} destinataire
          {passage.data.destinatairesPrevenus > 1 ? 's' : ''} prévenu
          {passage.data.destinatairesPrevenus > 1 ? 's' : ''}. Les envois suivent les canaux
          raccordés ; un canal qui ne l’est pas est tracé sans expédition.
        </div>
      )}

      {passage.error instanceof ErreurApi && <MessageErreur message={passage.error.message} />}

      <Carte titre="Règles d’alerte · activation par site et par profil" sansBordure>
        <p className="border-b border-gray-100 px-4 py-3 text-xs text-codialis-gray sm:px-6">
          Activation, antennes, destinataires, canaux et délais se règlent par la direction, dans « Paramétrage » ;
          chaque réglage y est tracé.
        </p>
        {isLoading && <Chargement libelle="Chargement des règles…" />}
        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={
                error instanceof ErreurApi
                  ? error.message
                  : 'Les règles d’alerte n’ont pas pu être chargées.'
              }
            />
          </div>
        )}

        {!isLoading && !error && (
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[780px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Active', 'Alerte', 'Échéance / déclencheur', 'Destinataires', 'Sites', 'Canal'].map(
                    (colonne) => (
                      <th
                        key={colonne}
                        className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                      >
                        {colonne}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.regles ?? []).map((regle) => (
                  <LigneRegle key={regle.code} regle={regle} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {compteurs && compteurs.nonEvaluees > 0 && (
          <div className="border-t border-gray-100 p-4 sm:p-6 text-[11px] text-codialis-gray">
            {compteurs.nonEvaluees > 1
              ? `${compteurs.nonEvaluees} règles sont déclarées mais pas encore évaluées automatiquement : ` +
                'elles n’ouvrent aucune alerte tant que leur évaluation n’est pas en place.'
              : 'Une règle est déclarée mais pas encore évaluée automatiquement : elle n’ouvre aucune ' +
                'alerte tant que son évaluation n’est pas en place.'}
          </div>
        )}
      </Carte>

      <Carte titre="Dernières alertes déclenchées" sansBordure>
        <div className="divide-y divide-gray-100">
          {total === 0 && (
            <p className="px-4 sm:px-6 py-10 text-center text-sm text-codialis-gray">
              Aucune alerte déclenchée sur ce périmètre
            </p>
          )}
          {dernieresAffichees.map((alerte) => (
            <LigneAlerteDeclenchee
              key={alerte.id}
              alerte={alerte}
              enCours={statuer.isPending}
              onStatuer={(statut) => statuer.mutate({ id: alerte.id, statut })}
            />
          ))}
        </div>
        {total > 0 && (
          <Pagination
            page={pageCourante}
            pages={pages}
            total={total}
            affiches={dernieresAffichees.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="alerte"
          />
        )}
      </Carte>
    </div>
  );
}

function LigneRegle({ regle }: { regle: RegleAlerte }): JSX.Element {
  return (
    <tr className="transition hover:bg-gray-50">
      <td className="px-4 py-3">
        <Pastille texte={regle.actif ? 'Active' : 'Coupée'} ton={regle.actif ? 'success' : 'neutral'} />
      </td>
      <td className="px-4 py-3">
        <div className="font-semibold text-codialis-navy">{regle.libelle}</div>
        {!regle.evalueeAutomatiquement && (
          <span className="mt-1 inline-block">
            <Pastille texte="Évaluation à venir" ton="warning" />
          </span>
        )}
        {regle.ouvertes > 0 && (
          <span className="ml-1 mt-1 inline-block">
            <Pastille texte={`${regle.ouvertes} ouverte(s)`} ton="danger" />
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-codialis-gray">{regle.declencheur}</td>
      <td className="px-4 py-3 text-xs text-codialis-gray">
        {regle.destinataires.map((destinataire) => destinataire.libelle).join(' · ')}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {regle.sites.map((site) => (
            <span
              key={site.code}
              className={`rounded-lg border px-2 py-1 text-[11px] font-bold ${
                site.actif
                  ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                  : 'border-gray-200 bg-gray-50 text-codialis-gray line-through'
              }`}
            >
              {site.code}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {regle.canaux.map((canal) => (
            <Pastille
              key={canal}
              texte={LIBELLES_CANAL[canal] ?? canal}
              ton={TONS_CANAL[canal] ?? 'neutral'}
            />
          ))}
        </div>
      </td>
    </tr>
  );
}

function LigneAlerteDeclenchee({
  alerte,
  enCours,
  onStatuer,
}: {
  alerte: AlerteDeclenchee;
  enCours: boolean;
  onStatuer: (statut: 'TRAITEE' | 'IGNOREE') => void;
}): JSX.Element {
  const ton =
    alerte.statut === 'OUVERTE'
      ? 'bg-red-500'
      : alerte.statut === 'TRAITEE'
        ? 'bg-codialis-teal'
        : 'bg-gray-300';

  return (
    <div className="flex items-start gap-3 px-4 sm:px-6 py-3">
      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${ton}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-codialis-navy">{alerte.libelleRegle}</div>
        <div className="text-[11px] text-codialis-gray">
          {alerte.message}
          {alerte.siteCode ? ` · ${alerte.siteCode}` : ''} ·{' '}
          {horodatage(alerte.declencheeLe)}
        </div>
      </div>
      {alerte.statut === 'OUVERTE' ? (
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => onStatuer('TRAITEE')}
            disabled={enCours}
            className="text-xs font-semibold text-codialis-sky hover:underline disabled:opacity-50"
          >
            Traitée
          </button>
          <button
            type="button"
            onClick={() => onStatuer('IGNOREE')}
            disabled={enCours}
            className="text-xs font-semibold text-codialis-gray hover:underline disabled:opacity-50"
          >
            Ignorer
          </button>
        </div>
      ) : (
        <Pastille
          texte={alerte.statut === 'TRAITEE' ? 'Traitée' : 'Ignorée'}
          ton={alerte.statut === 'TRAITEE' ? 'success' : 'neutral'}
        />
      )}
    </div>
  );
}
